// Outbox de mutaciones offline (F4 #52). Cola append-only en IndexedDB (vía Dexie) que
// guarda las mutaciones creadas SIN RED para reenviarlas, en orden, al reconectar. El runner
// (sync-runner.ts) la vacía FIFO contra POST /api/v1/sync/mutations; el servidor sigue siendo
// la única autoridad (revalida y audita cada mutación).
//
// Principios (PLAN.md §Sincronización — escrituras):
//   - Cada mutación lleva `clientMutationId` (UUID v7, clave de idempotencia del PUSH),
//     `entity`/`op`, `payload`, `baseVersion`, `status` y `attempts`. El orden FIFO lo fija
//     `seq` (autoincremental): garantiza que un LOTO creado offline se aplica ANTES que el
//     bloqueo que cuelga de él (dependencia causal).
//   - Las entidades creadas offline usan una identidad local (`clientUuid`); el `idMap`
//     traduce esa identidad al id de servidor en cuanto el alta se aplica, y el runner
//     reescribe con él las referencias pendientes de mutaciones posteriores.
//   - Vive en una DB Dexie DEDICADA (`loto-outbox`), separada de la caché de lecturas
//     (`loto-cache`, #51): purgar las lecturas en logout/401 NUNCA descarta el trabajo
//     encolado sin red.

import Dexie, { type Table } from "dexie";

// Entidades y operaciones sincronizables. Espejo EXACTO del vocabulario del PUSH backend
// (syncapi/push_dto.go): (entity, op) selecciona el applier del servidor.
export type OutboxEntity = "loto" | "bloqueo";
// `retirar_punto` (#245) es la operación de retirada vigente: el candado se libera punto a
// punto. `retirar` (candado entero) se CONSERVA solo para las mutaciones ya encoladas antes
// del despliegue —el cliente no vuelve a escribirlas—; el servidor las sigue aceptando y, si
// al candado le quedan puntos activos, las conflictúa con BLOQUEO_TIENE_PUNTOS_ACTIVOS.
export type OutboxOp = "create" | "retirar" | "retirar_punto";

// Estado de una mutación en la outbox (contrato con el runner y con el centro de conflictos, #53):
//   - pending:  encolada, aún no aplicada por el servidor.
//   - applied:  el servidor la aplicó; se elimina de la outbox tras registrar su id_mapping.
//   - conflict: chocó con el estado autoritativo (candado ya activo, LOTO cerrado, divergencia
//     de retiro…). Se conserva para que el centro de conflictos (#53) la muestre.
//   - rejected: inválida y no prosperará al reintentar (validación, permiso, op no soportada).
//   - blocked:  una referencia a una entidad creada offline no se pudo resolver porque su alta
//     no se aplicó (quedó en conflict/rejected). Queda huérfana a la espera del centro de conflictos.
export type OutboxStatus = "pending" | "applied" | "conflict" | "rejected" | "blocked";

// Una referencia pendiente a otra entidad creada offline. El runner reescribe `payload[field]`
// con el id de servidor en cuanto el `idMap` resuelve (entity, clientUuid). Es lo que permite
// encolar «colocar candado» sobre un LOTO que aún solo existe localmente: el `id_loto` del
// payload se rellena con el id real cuando el alta del LOTO se confirma.
export type OutboxRef = {
  field: string; // clave del payload a reescribir (p. ej. "id_loto")
  entity: OutboxEntity; // entidad referenciada
  clientUuid: string; // identidad local de la entidad referenciada
};

// OutboxMutation es una mutación encolada. `seq` (autoincremental, PK) fija el orden FIFO.
// `clientMutationId` es la clave de idempotencia del PUSH (UUID v7). `clientUuid` es la
// identidad local de la ENTIDAD creada (dedup del create + base del id_mapping); es "" en las
// operaciones que no crean entidad (retirar). `payload` es el cuerpo específico de (entity, op)
// que interpreta el applier del servidor. `resumen` es una frase en es-ES con la intención del
// técnico (p. ej. «Colocar candado CC-001 en LOTO-2026-0001»), denormalizada al encolar para que
// el centro de conflictos (#53) la muestre sin depender de cachés que el logout pudo purgar.
export type OutboxMutation = {
  seq?: number;
  // F7 · Multi-obra (#159): la obra a la que pertenece la mutación. El runner postea a
  // /obras/${idObra}/sync/mutations, de modo que la cola FIFO global sirve varias obras a la
  // vez sin mezclar sus endpoints. Los clientUuid/tempId siguen siendo globalmente únicos.
  idObra: number;
  clientMutationId: string;
  entity: OutboxEntity;
  op: OutboxOp;
  clientUuid: string;
  baseVersion: number | null;
  payload: Record<string, unknown>;
  refs: OutboxRef[];
  resumen?: string;
  status: OutboxStatus;
  errorCode?: string;
  attempts: number;
  createdAt: number;
};

// NewMutation es lo que aporta el caller al encolar: la outbox rellena `seq`, `status`,
// `attempts` y `createdAt`.
export type NewMutation = Omit<
  OutboxMutation,
  "seq" | "status" | "attempts" | "createdAt" | "refs"
> & {
  refs?: OutboxRef[];
};

// Fila del idMap: traduce la identidad local (entity+clientUuid) al id de servidor. La clave
// sigue siendo `entity:clientUuid` (los clientUuid son globalmente únicos); `idObra` se guarda
// solo para poder purgar el mapeo de una obra en una futura revocación de acceso (#159).
type IdMapRow = {
  key: string;
  idObra: number;
  entity: OutboxEntity;
  clientUuid: string;
  serverId: number;
};

function idMapKey(entity: OutboxEntity, clientUuid: string): string {
  return `${entity}:${clientUuid}`;
}

// Fila de localRefs: enlaza el id temporal NEGATIVO de una entidad creada de forma optimista sin
// red con su identidad local (`clientUuid`). Es el eslabón que permite la cadena causal offline:
// cuando se coloca un candado sobre un LOTO que aún solo existe localmente, la mutación del
// candado no conoce el id real del LOTO —solo su id temporal—, así que resuelve por aquí el
// `clientUuid` y encola una `OutboxRef` que el runner reescribe con el id de servidor tras el
// id_mapping del alta. Sobrevive a recargas (los ids temporales se basan en el reloj), de modo
// que la cadena se mantiene aunque la app se reabra sin conexión.
type LocalRefRow = {
  tempId: number;
  idObra: number;
  entity: OutboxEntity;
  clientUuid: string;
};

// OutboxAttachmentKind distingue a qué entidad pertenece una foto encolada: al BLOQUEO (foto de
// verificación del candado, #54) o al LOTO (registro fotográfico del LOTO, #211/#212). Gobierna el
// endpoint multipart al que el runner la sube y la entidad del idMap con la que resuelve su id real.
// Las filas viejas (anteriores a #212) no lo llevan: el runner las trata como "bloqueo" por defecto.
export type OutboxAttachmentKind = "bloqueo" | "loto";

// OutboxAttachment es una foto capturada sin conexión (o cuya subida online falló) que aún no se ha
// subido al servidor. El binario NO viaja en el PUSH (el applier de sync solo entiende JSON): se
// guarda aquí como Blob y el runner lo sube aparte, por multipart, en cuanto se conoce el id REAL de
// la entidad dueña. Según `kind` va a POST /bloqueos/{id}/fotos (foto de verificación, #54) o a
// POST /lotos/{id}/fotos (registro fotográfico del LOTO, #212). Ese id se resuelve por `clientUuid`
// (identidad local de la entidad creada offline → id_mapping) o directamente por `serverId` (la
// entidad ya existía y la foto quedó pendiente por un corte de red). Exactamente uno de los dos la
// identifica; el otro es null. `orden` es el orden de captura (el servidor asigna el ordinal
// definitivo por inserción). `fase` y `comentario` solo aplican a las fotos de LOTO (opcionales;
// para las de bloqueo van ausentes). Vive en la DB de la outbox (no en la de lecturas): sobrevive al
// logout como cualquier trabajo encolado sin red.
export type OutboxAttachment = {
  id?: number;
  // kind ausente ⇒ "bloqueo" (compatibilidad con filas encoladas antes de #212).
  kind?: OutboxAttachmentKind;
  // F7 · Multi-obra (#159): la obra de la entidad a la que pertenece la foto. El runner la sube a
  // /obras/${idObra}/{bloqueos|lotos}/{id}/fotos.
  idObra: number;
  clientUuid: string | null;
  serverId: number | null;
  blob: Blob;
  mime: string;
  orden: number;
  // Solo fotos de LOTO (#212): comentario opcional del registro fotográfico. `fase` quedó
  // OBSOLETO en #241 (la sella el servidor) pero se conserva como campo opcional IGNORADO
  // para no versionar Dexie: los adjuntos ya encolados en un cliente antiguo siguen siendo
  // legibles y el runner simplemente no lo envía.
  fase?: string;
  comentario?: string | null;
  createdAt: number;
};

// NewAttachment es lo que aporta el caller al encolar una foto: la outbox rellena `id` y `createdAt`.
export type NewAttachment = Omit<OutboxAttachment, "id" | "createdAt">;

// --- DB Dexie dedicada a la outbox ----------------------------------------------

class OutboxDB extends Dexie {
  outbox!: Table<OutboxMutation, number>;
  idMap!: Table<IdMapRow, string>;
  localRefs!: Table<LocalRefRow, number>;
  attachments!: Table<OutboxAttachment, number>;

  constructor() {
    super("loto-outbox");
    // `outbox`: PK `seq` autoincremental (orden FIFO) + índice por `status` (consulta de
    // pendientes). `idMap`: PK `key` (entity:clientUuid).
    this.version(1).stores({ outbox: "++seq, status", idMap: "key" });
    // v2 (#53): `localRefs` mapea id temporal → identidad local, para la cadena causal de
    // entidades creadas offline (alta de LOTO → colocación de candado sobre ese LOTO).
    this.version(2).stores({ localRefs: "tempId" });
    // v3 (#54): `attachments` guarda los BLOBs de las fotos de verificación capturadas offline,
    // ligadas al bloqueo por su identidad local (`clientUuid`) o por su id de servidor (`serverId`).
    // PK `id` autoincremental (orden de captura); índices por clientUuid/serverId para resolverlas.
    this.version(3).stores({ attachments: "++id, clientUuid, serverId" });
    // v4 (F7 #159): multi-obra. Cada fila gana `idObra`, indexado para poder purgar por obra en
    // una futura revocación de acceso. Como la app AÚN NO está en uso real (BD vacía, sin
    // operarios), el upgrade LIMPIA la outbox en vez de back-fillar el idObra de filas viejas
    // (que no tendrían obra a la que asignarse): bustear es seguro y evita reenviar mutaciones a
    // /obras/undefined/...
    this.version(4)
      .stores({
        outbox: "++seq, status, idObra",
        idMap: "key, idObra",
        localRefs: "tempId, idObra",
        attachments: "++id, clientUuid, serverId, idObra",
      })
      .upgrade(async (tx) => {
        await Promise.all([
          tx.table("outbox").clear(),
          tx.table("idMap").clear(),
          tx.table("localRefs").clear(),
          tx.table("attachments").clear(),
        ]);
      });
  }
}

const db = new OutboxDB();

// Pub/sub ligero: notifica a los suscriptores (el indicador de la cabecera) cada vez que la
// outbox cambia (encolar/actualizar/eliminar/purgar), para que el contador de pendientes se
// recalcule sin sondear.
const outboxListeners = new Set<() => void>();

/** subscribeOutbox registra un oyente de cambios en la outbox; devuelve la baja. */
export function subscribeOutbox(listener: () => void): () => void {
  outboxListeners.add(listener);
  return () => {
    outboxListeners.delete(listener);
  };
}

function notifyOutbox(): void {
  for (const listener of outboxListeners) {
    listener();
  }
}

/**
 * uuidv7 genera un UUID versión 7: 48 bits de marca de tiempo (ms Unix) big-endian seguidos de
 * bits aleatorios, con los nibbles de versión/variante fijados. Es monótono en el tiempo, así
 * que ordena de forma natural por creación —útil como clientMutationId (idempotencia) y como
 * clientUuid (identidad local)—. La aleatoriedad viene de crypto.getRandomValues.
 */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Marca de tiempo de 48 bits en bytes[0..5] (big-endian). Date.now() cabe con holgura en un
  // double, así que la división por potencias de 2 y el `& 0xff` (ToInt32, trunca) extraen cada
  // octeto sin pérdida.
  const ts = Date.now();
  bytes[0] = (ts / 2 ** 40) & 0xff;
  bytes[1] = (ts / 2 ** 32) & 0xff;
  bytes[2] = (ts / 2 ** 24) & 0xff;
  bytes[3] = (ts / 2 ** 16) & 0xff;
  bytes[4] = (ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;

  bytes[6] = (bytes[6] & 0x0f) | 0x70; // versión 7 en el nibble alto del byte 6
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122 (10xx) en el byte 8

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

// tempCounter desempata varias asignaciones dentro del mismo milisegundo. Es un contador de
// sesión acotado a [0,999]; la unicidad entre milisegundos (y entre recargas) la aporta el reloj.
let tempCounter = 0;

/**
 * nextTempId devuelve un id local NEGATIVO y único para una entidad optimista aún sin sincronizar.
 * Los ids de servidor son positivos, así que nunca colisionan. A diferencia de un simple contador
 * decreciente (que reiniciaría a −1 tras recargar y chocaría con un id temporal ya persistido en
 * la caché/outbox), se ancla al reloj —`Date.now()*1000 + contador`, negado—, de modo que es único
 * también entre recargas mientras el LOTO creado offline sobreviva en disco esperando sincronizar.
 */
export function nextTempId(): number {
  const disambiguator = tempCounter % 1000;
  tempCounter += 1;
  return -(Date.now() * 1000 + disambiguator);
}

// --- Operaciones de la outbox ----------------------------------------------------

/** enqueue añade una mutación al final de la cola (status pending). Devuelve la fila con su `seq`. */
export async function enqueue(m: NewMutation): Promise<OutboxMutation> {
  const row: OutboxMutation = {
    ...m,
    refs: m.refs ?? [],
    status: "pending",
    attempts: 0,
    createdAt: Date.now(),
  };
  const seq = await db.outbox.add(row);
  notifyOutbox();
  return { ...row, seq };
}

/** listAll devuelve todas las mutaciones en orden FIFO (por `seq`). */
export async function listAll(): Promise<OutboxMutation[]> {
  return db.outbox.orderBy("seq").toArray();
}

/** nextPending devuelve la primera mutación pendiente en orden FIFO, o undefined si no hay. */
export async function nextPending(): Promise<OutboxMutation | undefined> {
  return db.outbox
    .orderBy("seq")
    .filter((m) => m.status === "pending")
    .first();
}

/** countPending cuenta las mutaciones aún por sincronizar (pendientes), para el indicador. */
export async function countPending(): Promise<number> {
  return db.outbox.where("status").equals("pending").count();
}

/**
 * countUnsynced cuenta el trabajo no resuelto: pendientes de enviar MÁS las que quedaron en
 * conflicto/bloqueo/rechazo a la espera del centro de conflictos (#53). Es lo que el indicador
 * muestra como «pendientes» para no dar por sincronizado lo que en realidad requiere atención.
 */
export async function countUnsynced(): Promise<number> {
  const [mutaciones, fotos] = await Promise.all([
    db.outbox.where("status").anyOf("pending", "conflict", "rejected", "blocked").count(),
    db.attachments.count(),
  ]);
  return mutaciones + fotos;
}

/**
 * countConflicts cuenta solo lo que REQUIERE ATENCIÓN del técnico: mutaciones que el servidor
 * rechazó o que chocaron con el estado autoritativo, más las que quedaron bloqueadas por una
 * dependencia sin resolver. Es lo que distingue «sincronizando» (pendientes en vuelo, se resuelven
 * solas) de «hay conflictos por resolver» (el centro de conflictos, #53). Las pendientes NO cuentan.
 */
export async function countConflicts(): Promise<number> {
  return db.outbox.where("status").anyOf("conflict", "rejected", "blocked").count();
}

/** updateMutation aplica un parche parcial a una mutación por su `seq`. */
export async function updateMutation(seq: number, patch: Partial<OutboxMutation>): Promise<void> {
  await db.outbox.update(seq, patch);
  notifyOutbox();
}

/** removeMutation elimina una mutación de la cola (se usa al aplicarse con éxito). */
export async function removeMutation(seq: number): Promise<void> {
  await db.outbox.delete(seq);
  notifyOutbox();
}

/**
 * retryMutation devuelve una mutación no resuelta (conflict/rejected/blocked) al estado `pending`
 * y limpia su `errorCode`, para que el runner la reintente contra el servidor en el próximo flush.
 * Es la acción «Reintentar» del centro de conflictos (#53): el servidor sigue siendo la autoridad
 * (revalida y puede volver a conflictarla), pero el técnico decide reintentar en vez de descartar.
 * `attempts` se conserva como rastro del historial de intentos.
 */
export async function retryMutation(seq: number): Promise<void> {
  await db.outbox.update(seq, { status: "pending", errorCode: undefined });
  notifyOutbox();
}

/** getIdMapping resuelve la identidad local (entity+clientUuid) al id de servidor, si ya se conoce. */
export async function getIdMapping(
  entity: OutboxEntity,
  clientUuid: string,
): Promise<number | undefined> {
  const row = await db.idMap.get(idMapKey(entity, clientUuid));
  return row?.serverId;
}

/** putIdMapping registra la traducción identidad local → id de servidor tras aplicarse un alta. */
export async function putIdMapping(
  idObra: number,
  entity: OutboxEntity,
  clientUuid: string,
  serverId: number,
): Promise<void> {
  await db.idMap.put({ key: idMapKey(entity, clientUuid), idObra, entity, clientUuid, serverId });
}

/**
 * putLocalRef registra el enlace id temporal → identidad local de una entidad creada de forma
 * optimista sin red, para que una mutación posterior que la referencie (colocar candado sobre un
 * LOTO recién creado offline) pueda resolver su `clientUuid` y encolar una `OutboxRef`.
 */
export async function putLocalRef(
  tempId: number,
  idObra: number,
  entity: OutboxEntity,
  clientUuid: string,
): Promise<void> {
  await db.localRefs.put({ tempId, idObra, entity, clientUuid });
}

/** getLocalRef resuelve un id temporal a la identidad local (entity+clientUuid) que representa. */
export async function getLocalRef(
  tempId: number,
): Promise<{ entity: OutboxEntity; clientUuid: string } | undefined> {
  const row = await db.localRefs.get(tempId);
  return row ? { entity: row.entity, clientUuid: row.clientUuid } : undefined;
}

/**
 * getTempIdByClientUuid hace el camino inverso a putLocalRef: dado el id_mapping resuelto tras
 * aplicarse un alta (entity+clientUuid), devuelve el id temporal con el que la entidad se pintó de
 * forma optimista. El runner lo usa para retirar la ficha temporal de la caché tras sincronizar y
 * evitar un 404 al reconciliar (el id temporal no existe en el servidor).
 */
export async function getTempIdByClientUuid(
  entity: OutboxEntity,
  clientUuid: string,
): Promise<number | undefined> {
  const row = await db.localRefs
    .filter((r) => r.entity === entity && r.clientUuid === clientUuid)
    .first();
  return row?.tempId;
}

// --- Adjuntos (fotos de verificación offline, #54) -------------------------------

/**
 * enqueueAttachment encola una foto (Blob) —de verificación de un bloqueo (#54) o del registro
 * fotográfico de un LOTO (#212), según `kind`—, ligada a su entidad dueña por la identidad local
 * (`clientUuid`) o por el id de servidor (`serverId`). Devuelve el `id` autoincremental asignado.
 * Notifica a los oyentes para que el indicador cuente la foto como trabajo pendiente hasta subirla.
 */
export async function enqueueAttachment(a: NewAttachment): Promise<number> {
  const id = await db.attachments.add({ ...a, createdAt: Date.now() });
  notifyOutbox();
  return id;
}

/** listAttachments devuelve las fotos encoladas en orden de captura (por `id`). */
export async function listAttachments(): Promise<OutboxAttachment[]> {
  return db.attachments.orderBy("id").toArray();
}

/** removeAttachment elimina una foto encolada (tras subirla, o al descartarla como fallida). */
export async function removeAttachment(id: number): Promise<void> {
  await db.attachments.delete(id);
  notifyOutbox();
}

/** countAttachments cuenta las fotos aún por subir, para el indicador de sincronización. */
export async function countAttachments(): Promise<number> {
  return db.attachments.count();
}

/**
 * purgeOutbox vacía la outbox, el idMap, las refs locales y los adjuntos. NO se invoca en logout/401
 * (a diferencia de la caché de lecturas): el trabajo encolado sin red debe sobrevivir a una sesión
 * caducada. Se reserva para el purgado namespaceado por usuario (#53) y para los tests. Best-effort.
 */
export async function purgeOutbox(): Promise<void> {
  try {
    await Promise.all([
      db.outbox.clear(),
      db.idMap.clear(),
      db.localRefs.clear(),
      db.attachments.clear(),
    ]);
  } catch {
    // Best-effort: un fallo puntual de IndexedDB no debe bloquear al usuario.
  }
  notifyOutbox();
}
